import {
  BILLING_URL,
  COMPANY_URL,
  LOCAL_URL,
  SITE_URL,
} from "../../constants/apiUrls";
import { apiSlice } from "../apiSlice";

export const companyApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createCompany: builder.mutation({
      query: ({ data, token }) => (
        {
          url: `api/company/create/`,
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
          },
        }),
    }),
    // TIP-491 — raise a FREE, member-only verification request. The backend
    // gates this to active members (non-members get a 403 upsell).
    requestVerification: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/verify-request/`,
        method: "POST",
        body: { companyID },
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    // TIP-499 (MV Stage 1c) — persist the user's chosen default active company
    // per-Individual so it survives logout/login and follows them across
    // devices. Pass companyID: null to clear (revert to most-privileged).
    setDefaultCompany: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/individual-default-company/`,
        method: "POST",
        body: { companyID },
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    stripePaymentIntent: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/billing/stripe-payment/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createStripePayment: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/billing/stripe/create_payment/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateStripePayment: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/billing/stripe/update_payment/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    finalizeStripePayment: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/billing/stripe/finalize_payment/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    userSubscription: builder.mutation({
      query: ({ data }) => ({
        url: `api/company/subscription-status/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createPurchaseDetails: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/purchase-details`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
      /* The moment a Web Profile is bought, every cached read of "what has this viewer bought" is wrong. */
      invalidatesTags: (result, error, { data }) => [
        { type: "Purchase", id: data?.companyID },
        "User",
      ],
    }),

    createCompanyViewedDetails: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-viewed-details/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    getPurchaseDetails: builder.query({
      query: ({ userID, companyID, token }) => ({
        url: `api/company/purchase-details-get/${companyID}/${userID}/`,
        headers: {
          "content-type": "application/json",
        },
      }),
      /* Read by the company profile's action bar (useProfileActions) and its tab gate (profileAccess). */
      providesTags: (result, error, { companyID }) => [
        { type: "Purchase", id: companyID },
      ],
    }),
    getCompanyPurchaseDetails: builder.mutation({
      query: ({ userID, companyID, token }) => ({
        url: `api/company/purchase-details-get/${companyID}/${userID}/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCompanyWorldboxstatus: builder.mutation({
      query: ({ userID, companyID }) => ({
        url: `api/company/worldbox-status/${companyID}/${userID}/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCompanyPurchaseDetails: builder.mutation({
      query: ({ userID, companyID, token }) => ({
        url: `api/company/purchase-details-get/${companyID}/${userID}/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCompanyWorldboxstatus: builder.mutation({
      query: ({ userID, companyID }) => ({
        url: `api/company/worldbox-status/${companyID}/${userID}/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    getIndividual: builder.query({
      query: ({ token, individualID }) => ({
        url: `api/company/individual/${individualID}`,
        headers: {
          "content-type": "application/json",
        },
      }),
      /* individualSubscription[].no_of_profile_views is the credit balance useGetTheBusiness reads to decide whether the viewer can spend rather than buy. */
      providesTags: (result, error, { individualID }) => [
        { type: "User", id: individualID },
      ],
    }),

    getIndividualPrompt: builder.query({
      query: ({ token, promptID }) => ({
        url: `api/gpt_prompts/${promptID}/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    individualCompany: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/individual-company/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateCompany: builder.mutation({
      query: ({ data, companyId, token }) => ({
        url: `api/company/details/${companyId}`,

        method: "PATCH",
        body: data,
      }),
    }),
    updateCompanyDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-draft/`,
        method: "POST",
        body: data,
      }),
    }),
    updateCompanyDraftEdit: builder.mutation({
      query: ({ data, companyId, token, type }) => ({
        url:
          type === "socialmedia"
            ? `api/company/company-draft-socialmedia/${companyId}`
            : type === "aboutcompany"
              ? `api/company/company-draft/${companyId}`
              : type === "bannerlogo"
                ? `api/company/company-draft-bannerlogo/${companyId}`
                : `api/company/company-draft/${companyId}`,
        method: "PUT",
        body: data,
      }),
    }),
    fetchCompanyDraftEdit: builder.mutation({
      query: ({ data, companyId, token }) => ({
        url: `api/company/company-draft/${companyId}`,
        method: "GET",
      }),
    }),
    /* Authoritative "may I edit this company?". */
    getCompanyEditAccess: builder.query({
      query: (companyId) => ({
        url: `api/company/edit-access/${encodeURIComponent(companyId)}/`,
      }),
    }),

    getCompany: builder.query({
      query: ({ token, companyId }) => ({
        url: `api/company/company-details/${companyId}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCompanyReport: builder.mutation({
      query: ({ companyId, token }) => ({
        url: `api/company/details/report/${companyId}`,
        headers: {
          "content-type": "application/json",
        },

      }),
    }),

    getBPCompany: builder.query({
      query: ({ purchaseSubScriptionValid, isClaimed, token, companyId, role }) => ({
        url: `api/company/${(isClaimed || purchaseSubScriptionValid || role) ? 'details' : 'basic-details'
          }/${companyId}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getOptimisedCompanyDetails: builder.query({
      query: ({ token, companyId }) => ({
        url: `api/company/company-details/${companyId}`,
        headers: token ? {
          "content-type": "application/json",
        } :
          {
            "content-type": "application/json",
          },
      }),
      /* This is the company profile screen's own read (useCompanyProfile).
         Claiming the company changes the answer — `claimed`, the owner, and
         the action bar that keys off them — so the read has to be taggable
         or the pre-claim copy survives until a hard refresh. */
      providesTags: (result, error, { companyId }) => [
        { type: "Company", id: companyId },
      ],
    }),
    getCompanyDetails: builder.mutation({
      query: ({ token, companyId }) => ({
        url: `api/company/details/${companyId}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCurrentUser: builder.query({
      query: ({ token }) => ({
        url: `api/api/user/me/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    searchTrevexCompany: builder.query({
      query: ({ token, searchTerm, code }) => ({
        url: `api/company/details/?search=${searchTerm}&country_code=${code}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createFinance: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/financial/`,
        method: "POST",
        body: data,
      }),
    }),
    createFinanceDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/financial-draft/`,
        method: "POST",
        body: data,
      }),
    }),
    updateFinanceDraftDetails: builder.mutation({
      query: ({ data, token, company_financialID }) => ({
        url: `api/company/financial-draft/${company_financialID}`,
        method: "PATCH",
        body: data,
      }),
    }),
    createFinanceDraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/finanicaldraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    createInsuranceDraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/insurancedraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    createOwnershipDraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/ownershipdetailsdraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    businessRelationDraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/businessrelationsdraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    relatedEntityDraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/relatedentitydraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    createauthorizedsignDraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/authorizedsigndraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    bankdetailsdraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/bankdetailsdraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    keypersonneldetailsdraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/keypersonneldetailsdraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    addressdetailsdraftFetch: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/addressdetailsdraft?current_companyID=${companyID}`,
        method: "GET",
      }),
    }),
    updateFinance: builder.mutation({
      query: ({ data, company_financialID, token }) => ({
        url: `api/company/financial/${company_financialID}`,
        method: "PATCH",
        body: data,
      }),
    }),

    getFinancial: builder.query({
      query: ({ token, company_financialID }) => ({
        url: `api/company/financial/${company_financialID}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getBankData: builder.query({
      query: ({ token, bankID }) => ({
        url: `api/company/bank/${bankID}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getBankByIban: builder.mutation({
      query: ({ iban, token }) => ({
        url: `api/company/bank/iban/?iban=${iban}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateFinanceDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/financial-draft/`,
        method: "POST",
        body: data,
      }),
    }),
    createCompanyFinance: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/financial/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createCompanyFinanceDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/financial-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    uploadFinanceDocument: builder.mutation({
      query: ({ data, token, company_id }) => ({
        url: `api/company/financial-data/${company_id}/upload_data/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateFinanceCurrency: builder.mutation({
      query: ({ data, token, companyId }) => ({
        url: `api/company/financial-data/${companyId}/update_currency_units/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    uploadFinanceDocuments: builder.mutation({
      query: ({ data, token, companyId, location = 'upload' }) => ({
        url: `api/company/financial-data/${companyId}/financial_document/?location=${location}`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getDocumentToFillData: builder.mutation({
      query: ({ token, companyId }) => ({
        url: `api/company/financial-data/${companyId}/get_financial_document/`,
        method: "GET",
      }),
    }),
    uploadFinanceDocumentsData: builder.mutation({
      query: ({ data, token, companyId }) => ({
        url: `api/company/financial-data/${companyId}/upload_data/?filled-form=true`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCompanyFinanceChartData: builder.mutation({
      query: ({ token, company_id, location }) => ({
        url: `api/company/financial-data/${company_id}/get_graph/?location=${location}`,
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createCompanyFollow: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-follow/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteCompanyFollow: builder.mutation({
      query: ({ id, token }) => ({
        url: `api/company/delete_company_follow/${id}/`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createCompanyFollowUpdate: builder.mutation({
      query: ({ companyID, content, token }) => ({
        url: `api/company/company-updates/`,
        method: "POST",
        body: JSON.stringify({
          companyID: companyID,
          content: content,
        }),
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCompanyUpdates: builder.query({
      query: ({ companyID, token }) => ({
        url: `api/company/company-updates/?companyID=${companyID}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateViewedTimestamp: builder.mutation({
      query: ({ followID, token }) => ({
        url: `api/company/update-viewed-timestamp/${followID}/`,
        method: "PUT",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createAddressDetails: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-address-details/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    editAddressDetailsDraft: builder.mutation({
      query: ({ data, token, company_address_details_draftID }) => ({
        url: `api/company/company-address-details-draft/${company_address_details_draftID}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createAddressDetailsDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-address-details-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    authorizedSign: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/authorized-sign/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    authorizedSignDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/authorized-sign-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateAuthorizedSign: builder.mutation({
      query: ({ data, authorized_signID, token }) => ({
        url: `api/company/authorized-sign/${authorized_signID}`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateAuthorizedSignDraft: builder.mutation({
      query: ({ data, token, authorized_signID }) => ({
        url: `api/company/authorized-sign-draft/${authorized_signID}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    createAuthorizedSign: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-authorized-sign/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createAddressDetailsUpdate: builder.mutation({
      query: ({ data, token, company_address_detailsID }) => ({
        url: `api/company/company-address-details/${company_address_detailsID}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createAddress: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-address/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    industry: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/industry/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createInsurance: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/insurance/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createInsuranceDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/insurance-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateInsuranceDraft: builder.mutation({
      query: ({ data, insuranceID, token }) => ({
        url: `api/company/insurance-draft/${insuranceID}`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateInsurance: builder.mutation({
      query: ({ data, insuranceID, token }) => ({
        url: `api/company/insurance/${insuranceID}`,
        method: "PATCH",
        body: data,
      }),
    }),

    createOwnershipCompanyDetails: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/ownership-company-details/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createOwnershipCompanyDetailsDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/ownership-company-details-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateOwnershipCompanyDetails: builder.mutation({
      query: ({ data, ownership_compId, token }) => ({
        url: `api/company/ownership-company-details/${ownership_compId}`,
        method: "PATCH",
        body: data,
      }),
    }),
    createOwnershipIndividual: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/ownership-individual/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    createOwnershipIndividualDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/ownership-individual-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateOwnershipCompanyDetailsDraft: builder.mutation({
      query: ({ data, token, ownershipdraftID }) => ({
        url: `api/company/ownership-company-details-draft/${ownershipdraftID}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateOwnershipIndividualDraft: builder.mutation({
      query: ({ data, token, ownershipdraftID }) => ({
        url: `api/company/ownership-individual-draft/${ownershipdraftID}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    updateOwnershipIndividual: builder.mutation({
      query: ({ data, ownership_indId, token }) => ({
        url: `api/company/ownership-individual/${ownership_indId}`,
        method: "PATCH",
        body: data,
      }),
    }),
    createOwnership: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/ownership/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createOwnershipCompany: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/ownership-company/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteOwnershipCompanyDetails: builder.mutation({
      query: ({ companyID, token }) => ({
        url: `api/company/delete_ownership/${companyID}/`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createKeyPersonnel: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/key-personnel/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createKeyPersonnelDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/key-personnel-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    updateKeyPersonnel: builder.mutation({
      query: ({ data, key_personnelId, token }) => ({
        url: `api/company/key-personnel/${key_personnelId}`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateKeyPersonnelDraft: builder.mutation({
      query: ({ data, token, key_personnelId }) => ({
        url: `api/company/key-personnel-draft/${key_personnelId}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createCompanyKeyPersonnel: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/company-key-personnel/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    createRelatedEntity: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/related-entity/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    createRelatedEntityDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/related-entity-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    updateRelatedEntity: builder.mutation({
      query: ({ data, related_entityId, token }) => ({
        url: `api/company/related-entity/${related_entityId}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    updateRelatedEntityDraft: builder.mutation({
      query: ({ data, token, related_entityId }) => ({
        url: `api/company/related-entity-draft/${related_entityId}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    createBusinessRelation: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/business-relation/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    createBusinessRelationDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/business-relation-draft/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    updateBusinessRelation: builder.mutation({
      query: ({ data, business_relationId, token }) => ({
        url: `api/company/business-relation/${business_relationId}`,
        method: "PATCH",
        body: data,
      }),
    }),

    updateBusinessRelationDraft: builder.mutation({
      query: ({ data, token, business_relationId }) => ({
        url: `api/company/business-relation-draft/${business_relationId}`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    createBank: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/bank/`,
        method: "POST",
        body: data,
      }),
    }),
    createBankDraft: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/bank-draft/`,
        method: "POST",
        body: data,
      }),
    }),
    updateBank: builder.mutation({
      query: ({ data, bankId, token }) => ({
        url: `api/company/bank/${bankId}`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateBankDraft: builder.mutation({
      query: ({ data, bankId, token }) => ({
        url: `api/company/bank-draft/${bankId}`,
        method: "PATCH",
        body: data,
      }),
    }),
    GetClaimed: builder.mutation({
      query: ({ data, token }) => {
        if (!token) {
          throw new Error("Authorization token is missing.");
        };
        return {
          url: `api/admins/company-admin-request/`,
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
          },
        };
      },
    }),
    getCompanyClaimed: builder.mutation({
      query: ({ data, token }) => {
        if (!token) {
          throw new Error("Authorization token is missing.");
        };
        return {
          url: `api/company/claimdraft/create_claim/`,
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
          },
        };
      },
      /* ICC-01 hands the company over the moment this resolves, so the
         viewer's individual record — which is where My Businesses reads its
         list from — is stale as soon as the claim succeeds. Without this the
         company the user just claimed is missing until a hard refresh.

         "Company" for the same reason one level down: the profile the user
         is sent to next caches the company as it was before the claim, so
         it rendered as unclaimed — still offering the Claim button — until
         the page was reloaded by hand. Invalidated by type rather than by
         id: the claim is made against the route's id, which for a WorldBox
         record is a WIN and not the companyID that comes back, so keying
         the invalidation would miss exactly the case that creates a new
         company. Claims are rare; refetching every cached company is not a
         cost worth being clever about. */
      invalidatesTags: ["User", "Company"],
    }),
    /* ICC-11/ICC-12 — disputing a claim on a company someone else holds.
       Creates a Support ticket in the "Claim Dispute" category; the current
       owner is deliberately not notified, support investigates first. */
    createClaimDispute: builder.mutation({
      query: ({ data, token }) => {
        if (!token) {
          throw new Error("Authorization token is missing.");
        };
        return {
          url: `api/company/claim-dispute/`,
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
          },
        };
      },
      /* A dispute IS a Support ticket, and the viewer's own record carries
         the list Help & Support renders (individualSupport). Without this,
         "Go to Help & Support" straight off the dispute receipt showed a
         list with the ticket the user had just raised missing from it. */
      invalidatesTags: ["User"],
    }),
    getCompanyUserList: builder.mutation({
      query: ({ data, token }) => {
        return {
          url: `api/company/individual-company-invite/get_list/`,
          method: "POST",
          body: { company_id: data.id },
          headers: {
            "content-type": "application/json",
          },
        };
      },
    }),
    createCompanyUserInvite: builder.mutation({
      query: ({ data, token }) => {
        if (!token) {
          throw new Error("Authorization token is missing.");
        }
        return {
          url: `api/company/individual-company-invite/invite_user/`,
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
          },
        };
      },
    }),
    createCompanyUserRoleUpdate: builder.mutation({
      query: ({ data, token }) => {
        return {
          url: `api/company/individual-company-invite/change_role/`,
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
          },
        };
      },
    }),
    createCompanyUserRoleDelete: builder.mutation({
      query: ({ data, token }) => {
        return {
          url: `api/company/individual-company-invite/delete_role/`,
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
          },
        };
      },
    }),
    createWorldBoxOrder: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/wordbox-order/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
      /* The other outcome of buying a listed profile: no profile to open yet,
         a report being built. The masthead must move to "Request in progress"
         rather than keep offering the purchase. */
      invalidatesTags: (result, error, { data }) => [
        { type: "Purchase", id: data?.companyID },
      ],
    }),
    createWorldBoxCompany: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/wordbox-order-without-win/create_order/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
        credentials: 'include',
      }),
    }),
    getCompanyRequestStatus: builder.mutation({
      query: ({ companyID, individualID, token }) => ({
        url: `api/admins/company-admin-request-status?company_id=${companyID}&individual_id=${individualID}`,
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    updateCompanyRequestStatus: builder.mutation({
      query: ({ requestID, token }) => ({
        url: `api/admins/update-admin-status?requestID=${requestID}`,
        method: "PUT",
        body: { status: "Modified" },
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    getUserPurchaedData: builder.query({
      query: ({ token, id }) => ({
        url: `api/company/user-purchased-companies?id=${id}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    getUserSubscriptionList: builder.query({
      query: ({ individualID, token }) => ({
        url: `api/company/subscription-list/${individualID}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    getVerifyCompanyPayment: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/company/verify-company-payment`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getVerifyCompanyPaymentFetch: builder.query({
      query: ({ individualID, token }) => ({
        url: `api/company/verify-company-payment-list/${individualID}`,
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    getVerifyCompanyPaymentEmail: builder.mutation({
      query: ({ token, CompanyID, individualID }) => ({
        url: `api/company/verify-company-payment-email/${CompanyID}/${individualID}`,
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteBankDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-bank-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteInsuranceDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-insurance-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteBusinessRelationDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-businessrelation-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteRelatedEntityDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-relatedentity-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteKeyPersonnelDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-keypersonnel-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteAddressDetailsDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-address-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteAuthSignDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-authsign-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteOwnershipCompDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-ownership-company-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteOwnershipIndDraft: builder.mutation({
      query: (data) => ({
        url: `api/company/delete-ownership-individual-draft/${data.data}`,
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),

    addEndorsement: builder.mutation({
      query: ({ companyId, endorsementData, token }) => ({
        url: `api/company/endorsements/${companyId}/add/`,
        method: 'POST',
        body: endorsementData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    deleteEndorsement: builder.mutation({
      query: ({ endorsementId, token }) => ({
        url: `api/company/endorsements/remove/${endorsementId}/`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    // TIP-504 — member-gated endorsement view. Pass the viewer's active company
    // so the backend (TIP-492) decides member (returns `endorsements`) vs
    // non-member (returns a teaser: `endorsement_count` + `message`).
    getEndorsements: builder.query({
      query: ({ companyId, actingCompanyId, token }) => ({
        url: `api/company/endorsements/${companyId}/?list&acting_company_id=${actingCompanyId ?? ''}`,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getActiveEnterprise: builder.query({
      query: ({ token }) => ({
        url: `api/enterprise/active-enterprise-solutions/?page=${1}&page_size=${100}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getTrackCompanyList: builder.query({
      query: ({ token, id, page, limit, searchTerm }) => ({
        url: `api/enterprise/active-enterprise-tracked/${id}/?company_name=${searchTerm}&page=${page}&page_size=${limit}`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getUpdateViewedTime: builder.mutation({
      query: ({ token, id }) => ({
        url: `api/enterprise/update_viewed_time/${id}/`,
        method: 'GET',
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    addCompanyToEnterprise: builder.mutation({
      query: ({ token, data }) => ({
        url: `${LOCAL_URL}/enterprise/add-company-to-enterprise/`,
        method: "POST",
        body: data,
      }),
    }),
    createOrderForEnterprise: builder.mutation({
      query: ({ token, data }) => ({
        url: `${LOCAL_URL}/enterprise/create-order-for-company/`,
        method: "POST",
        body: data,
      }),
    }),
    requestDemoTriggerForAdmin: builder.mutation({
      query: ({ requestData }) => ({
        url: `api/company/calendly/invite-details/`,
        method: 'POST',
        body: requestData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    createCorporateTax: builder.mutation({
      query: ({ token, data }) => ({
        url: `api/company/corporate-tax/dashboard/create_tax/`,
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getAvailableCategory: builder.query({
      query: ({ token, id }) => ({
        url: `api/company/corporate-tax/dashboard/available_categories/?company_id=${id}`,
        method: 'GET',
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getCategoryList: builder.mutation({
      query: ({ token, id }) => ({
        url: `api/company/corporate-tax/dashboard/get_list/`,
        method: 'POST',
        body: { company_id: id },
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateCategory: builder.mutation({
      query: ({ token, data }) => ({
        url: `api/company/corporate-tax/dashboard/edit_tax/`,
        method: 'POST',
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    deleteCategory: builder.mutation({
      query: ({ token, data }) => ({
        url: `api/company/corporate-tax/dashboard/delete_tax/`,
        method: 'POST',
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getMagicToken: builder.mutation({
      query: ({ token }) => ({
        url: `api/user/magic-token/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getSubIndustriesData: builder.query({
      query: ({ token }) => ({
        url: `api/company/sub-industry/`,
        method: "GET",
      }),
      providesTags: ['SubIndustries'],
      keepUnusedDataFor: 300,
    }),
    updateClaimDraft: builder.mutation({
      query: ({ token, data, url }) => ({
        url: `api/company/${url}/`,
        method: "POST",
        body: data,
      }),
    }),
    updateTradeLicense: builder.mutation({
      query: ({ data }) => ({
        url: `api/company/trade-license-request/upload_request/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getTraderData: builder.query({
      query: ({ fileToken }) => ({
        url: `api/company/trade-license-request/${fileToken}/get_information/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getAssignedReferenceRequests: builder.query({
      query: (token) => ({
        url: `/api/payment-reference/user/get-assigned-reference-requests/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getReferenceWithAnswers: builder.mutation({
      query: ({token, id}) => ({
        url: `/api/payment-reference/user/submit-reference/${id}/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    submitReferenceWithAnswers: builder.mutation({
      query: ({token, id, data}) => ({
        url: `/api/payment-reference/user/submit-reference/${id}/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getAssignedReferenceQuestions: builder.query({
      query: ({token, id}) => ({
        url: `/api/payment-reference/user/reference-questions/${id}/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getEnterpriseSolutionStats: builder.mutation({
      query: ({token, id}) => ({
        url: `/api/enterprise/enterprise-solution-stats/${id}/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    uploadEnterpriseLink: builder.mutation({
      query: ({data, token}) => ({
        url: `/api/enterprise/upload-enterprise-file/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
  }),
});

export const {
  useGetCompanyEditAccessQuery,
  useGetUserPurchaedDataQuery,
  useCreateCompanyMutation,
  useRequestVerificationMutation,
  useSetDefaultCompanyMutation,
  useGetUserSubscriptionListQuery,
  useGetCompanyQuery,
  useGetCurrentUserQuery,
  useGetFinancialQuery,
  useGetIndividualQuery,
  useGetIndividualPromptQuery,
  useSearchTrevexCompanyQuery,
  useIndustryMutation,
  useUpdateCompanyMutation,
  useUpdateCompanyDraftMutation,
  useUpdateCompanyDraftEditMutation,
  useFetchCompanyDraftEditMutation,
  useCreateAddressDetailsMutation,
  useCreateAddressDetailsDraftMutation,
  useEditAddressDetailsDraftMutation,
  useCreateAddressMutation,
  useCreateAddressDetailsUpdateMutation,
  useCreatePurchaseDetailsMutation,
  useGetPurchaseDetailsQuery,
  useGetCompanyPurchaseDetailsMutation,
  useAuthorizedSignMutation,
  useGetBPCompanyQuery,
  useGetOptimisedCompanyDetailsQuery,
  useAuthorizedSignDraftMutation,
  useCreateAuthorizedSignMutation,
  useUpdateAuthorizedSignMutation,
  useUpdateAuthorizedSignDraftMutation,
  useGetVerifyCompanyPaymentMutation,
  useGetVerifyCompanyPaymentEmailMutation,
  useCreateFinanceMutation,
  useCreateFinanceDraftMutation,
  useCreateFinanceDraftFetchMutation,
  useCreateCompanyFinanceMutation,
  useUploadFinanceDocumentMutation,
  useUpdateFinanceCurrencyMutation,
  useUploadFinanceDocumentsMutation,
  useGetDocumentToFillDataMutation,
  useUploadFinanceDocumentsDataMutation,
  useCreateCompanyFinanceDraftMutation,
  useUpdateFinanceMutation,
  useGetCompanyFinanceChartDataMutation,
  useUpdateFinanceDraftMutation,
  useIndividualCompanyMutation,
  useCreateInsuranceMutation,
  useUpdateInsuranceMutation,
  useCreateOwnershipCompanyMutation,
  useCreateOwnershipMutation,
  useCreateOwnershipIndividualMutation,
  useUpdateOwnershipIndividualMutation,
  useCreateOwnershipCompanyDetailsMutation,
  useUpdateOwnershipCompanyDetailsMutation,
  useCreateKeyPersonnelMutation,
  useCreateKeyPersonnelDraftMutation,
  useCreateCompanyKeyPersonnelMutation,
  useUpdateKeyPersonnelMutation,
  useUpdateKeyPersonnelDraftMutation,
  useCreateRelatedEntityMutation,
  useCreateRelatedEntityDraftMutation,
  useUpdateRelatedEntityMutation,
  useUpdateRelatedEntityDraftMutation,
  useCreateBusinessRelationMutation,
  useCreateBusinessRelationDraftMutation,
  useUpdateBusinessRelationMutation,
  useUpdateBusinessRelationDraftMutation,
  useCreateBankMutation,
  useCreateBankDraftMutation,
  useUpdateBankMutation,
  useUpdateBankDraftMutation,
  useCreateCompanyFollowMutation,
  useDeleteCompanyFollowMutation,
  useCreateCompanyFollowUpdateMutation,
  useGetCompanyUpdatesQuery,
  useUpdateViewedTimestampMutation,
  useCreateOwnershipCompanyDetailsDraftMutation,
  useGetClaimedMutation,
  useGetCompanyClaimedMutation,
  useCreateClaimDisputeMutation,
  useCreateOwnershipIndividualDraftMutation,
  useStripePaymentIntentMutation,
  useCreateStripePaymentMutation,
  useUpdateStripePaymentMutation,
  useFinalizeStripePaymentMutation,
  useUserSubscriptionMutation,
  useGetBankDataQuery,
  useGetBankByIbanMutation,
  useCreateInsuranceDraftMutation,
  useCreateCompanyViewedDetailsMutation,
  useGetCompanyUserListMutation,
  useCreateCompanyUserInviteMutation,
  useCreateCompanyUserRoleUpdateMutation,
  useCreateCompanyUserRoleDeleteMutation,
  useCreateWorldBoxOrderMutation,
  useCreateWorldBoxCompanyMutation,
  useGetCompanyDetailsMutation,
  useGetCompanyRequestStatusMutation,
  useUpdateCompanyRequestStatusMutation,
  useUpdateFinanceDraftDetailsMutation,
  useCreateInsuranceDraftFetchMutation,
  useCreateOwnershipDraftFetchMutation,
  useBusinessRelationDraftFetchMutation,
  useRelatedEntityDraftFetchMutation,
  useCreateauthorizedsignDraftFetchMutation,
  useBankdetailsdraftFetchMutation,
  useKeypersonneldetailsdraftFetchMutation,
  useAddressdetailsdraftFetchMutation,
  useGetCompanyWorldboxstatusMutation,
  useDeleteOwnershipCompanyDetailsMutation,
  useUpdateInsuranceDraftMutation,
  useUpdateOwnershipCompanyDetailsDraftMutation,
  useUpdateOwnershipIndividualDraftMutation,
  useGetVerifyCompanyPaymentFetchQuery,
  useDeleteBankDraftMutation,
  useDeleteInsuranceDraftMutation,
  useDeleteBusinessRelationDraftMutation,
  useDeleteRelatedEntityDraftMutation,
  useDeleteKeyPersonnelDraftMutation,
  useDeleteAddressDetailsDraftMutation,
  useDeleteAuthSignDraftMutation,
  useDeleteOwnershipCompDraftMutation,
  useDeleteOwnershipIndDraftMutation,
  useGetCompanyReportMutation,
  useAddEndorsementMutation,
  useDeleteEndorsementMutation,
  useGetEndorsementsQuery,
  useGetActiveEnterpriseQuery,
  useGetTrackCompanyListQuery,
  useGetUpdateViewedTimeMutation,
  useAddCompanyToEnterpriseMutation,
  useCreateOrderForEnterpriseMutation,
  useRequestDemoTriggerForAdminMutation,
  useCreateCorporateTaxMutation,
  useGetAvailableCategoryQuery,
  useGetCategoryListMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetMagicTokenMutation,
  useGetSubIndustriesDataQuery,
  useUpdateClaimDraftMutation,
  useUpdateTradeLicenseMutation,
  useGetTraderDataQuery,
  useGetAssignedReferenceRequestsQuery,
  useGetReferenceWithAnswersMutation,
  useSubmitReferenceWithAnswersMutation,
  useGetAssignedReferenceQuestionsQuery,
  useGetEnterpriseSolutionStatsMutation,
  useUploadEnterpriseLinkMutation
} = companyApiSlice;
